// StripeAccounts list page — CHG-1642 / ITER-3752.
//
// Wave 1 Tier 1 v1→v2 port. Mirrors web/dashboard/src/pages/
// StripeAccounts.tsx columns + empty-state but consumes v2's ListPage
// primitive directly. Hand-rolled inline config (no entity-hint
// generator wiring yet) — documented exception per
// feedback_deterministic_codegen_over_hand_rolling.md: admin port, no
// existing entity-hint, OAuth-callback toast pattern (separate from
// the v2 generator's standard list shape) makes hand-rolling the
// faster path for the v1 parity slice. Generator wiring can land
// later if Wave 2 needs additional Stripe-account pages.

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ListPage, type ListPageConfig } from "../components/composition/ListPage";
import {
  listStripeAccounts,
  type StripeAccount,
} from "../api/stripe-accounts";
import { useFormatters } from "../contexts/IntlContext";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  active: "Active",
  restricted: "Restricted",
  rejected: "Rejected",
  disabled: "Disabled",
};

export function StripeAccounts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fmt = useFormatters();

  // v1-parity: backend OAuth callback redirects to
  // /stripe-accounts?stripe=connected on success. Surface a toast
  // once + strip the param so a page refresh doesn't re-fire.
  // v2 has no react-hot-toast equivalent in scope yet — emit a
  // window CustomEvent so any future toast host can pick it up;
  // also invalidate the list so the freshly-connected row appears.
  useEffect(() => {
    const stripeParam = searchParams.get("stripe");
    if (!stripeParam) return;
    if (stripeParam === "connected") {
      queryClient.invalidateQueries({ queryKey: ["stripe-accounts", "list"] });
      window.dispatchEvent(
        new CustomEvent("v2-toast", {
          detail: { kind: "success", text: "Stripe account connected" },
        }),
      );
    } else if (stripeParam === "error") {
      const detail =
        searchParams.get("detail") ||
        "Stripe connection failed. Please try again.";
      window.dispatchEvent(
        new CustomEvent("v2-toast", { detail: { kind: "error", text: detail } }),
      );
    }
    const next = new URLSearchParams(searchParams);
    next.delete("stripe");
    next.delete("detail");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const config: ListPageConfig<StripeAccount> = {
    title: "Stripe Accounts",
    subtitle: "Connected Stripe payment accounts",
    queryKey: ["stripe-accounts", "list"] as const,
    queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
      listStripeAccounts({ page, pageSize, sortField, sortDirection, search }),
    pageSize: 25,
    rowHref: "/stripe-accounts/{id}",
    columns: [
      { field: "organization_name", label: "Organization", type: "text" },
      {
        field: "account_status",
        label: "Status",
        type: "custom",
        render: (row) => STATUS_LABEL[row.account_status] ?? row.account_status,
      },
      {
        field: "charges_enabled",
        label: "Charges",
        type: "custom",
        render: (row) => (row.charges_enabled ? "Enabled" : "Disabled"),
      },
      {
        field: "payouts_enabled",
        label: "Payouts",
        type: "custom",
        render: (row) => (row.payouts_enabled ? "Enabled" : "Disabled"),
      },
      {
        field: "stripe_account_id",
        label: "Account ID",
        type: "text",
        sortable: true,
      },
      {
        field: "created_at",
        label: "Created",
        type: "custom",
        render: (row) =>
          row.created_at ? fmt.formatDate(row.created_at, "short") : "—",
      },
    ],
    emptyState: {
      title: "No payment accounts connected",
      subtitle:
        "Connect a Stripe account to accept online card payments through your donate pages, widgets, and kiosks.",
    },
  };

  return <ListPage config={config} />;
}
